import { Trans } from '@lingui/react';
import FeedbackIcon from '@mui/icons-material/Feedback';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Tooltip
} from '@mui/material';
import Alert from '@mui/material/Alert';
import ProgressButton from 'components/ProgressButton';
import { useFormik } from 'formik';
import html2canvas from 'html2canvas';
import { i18n } from 'i18n';
import { useAddImageMutation, useCreateContentItemMutation } from 'providers/api/useContentApi';
import { useProjectQuery } from 'providers/api/useProjects';
import React, { useState } from 'react';

interface UserFeedback {
  feedback?: string;
  userId: string;
  issueUrl: string;
  screenshot?: { fileName: string; data: string | null };
}

const dataUrlToFile = (dataUrl: string, fileName: string): File => {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] || '';
  const binary = atob(base64);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    array[i] = binary.charCodeAt(i);
  }
  return new File([array], fileName, { type: mime });
};

interface UserFeedbackIconProps {
  userId: string;
  userName: string;
  userEmail: string;
  currentUrl: string;
  projectId?: string;
}

const UserFeedbackIcon = ({
  userId,
  userName,
  userEmail,
  currentUrl,
  projectId,
}: UserFeedbackIconProps) => {
  const [fileParameter, setFileParameter] = useState<{ data: File; fileName: string } | null>(null);
  const [open, setOpen] = React.useState(false);
  const [feedbackComplete, setFeedbackComplete] = React.useState(false);
  const [feedbackError, setFeedbackError] = React.useState(false);
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const { data: feedbackProject } = useProjectQuery(projectId ?? '', { enabled: !!projectId });
  const mutation = useCreateContentItemMutation();
  const { mutate: uploadImage } = useAddImageMutation();

  const formik = useFormik<UserFeedback>({
    initialValues: {
      feedback: '',
      userId,
      issueUrl: currentUrl,
      screenshot: { fileName: '', data: null },
    },
    onSubmit: async (userFeedback, { setSubmitting }) => {
      setSubmitting(true);

      if (fileParameter) {
        uploadImage(fileParameter, {
          onSuccess: (uploadedUrl) => {
            const newContentItem = {
              ContentType: 'UserFeedback',
              UserFeedback: {
                Text: {
                  Text: userFeedback.feedback,
                },
                UserId: {
                  Text: userFeedback.userId,
                },
                UserName: {
                  Text: userName,
                },
                UserEmail: {
                  Text: userEmail,
                },
                Url: {
                  Text: window.location.href,
                },
                ProjectId: {
                  Text: projectId ?? 'N/A',
                },
                Mine: {
                  Text: feedbackProject?.name ?? 'Unknown',
                },
                ScreenShotURL: {
                  // EXAMPLE: /app/admin/user-feedback-admin-page?image=userfeedback/feedbackimage-847zx9emkmgyja8xv3nn6ypz7z.png
                  Text: `/app/admin/user-feedback-admin-page?image=${uploadedUrl}`,
                },
              },
            };

            mutation.mutate(newContentItem as any, {
              onSettled: () => {
                setSubmitting(false);
              },
              onSuccess: () => {
                setFeedbackComplete(true);
              },
              onError: () => {
                setFeedbackError(true);
              },
            });
          },
        });
      }
    },
  });

  const captureScreenshot = async () => {
    const canvas = await html2canvas(document.body);
    const dataUrl = canvas.toDataURL('image/png');
    setImagePreview(dataUrl);
    const fileName = `screenshot-${Date.now()}.png`;
    const file = dataUrlToFile(dataUrl, fileName);

    setFileParameter({
      data: file,
      fileName,
    });
  };

  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    isSubmitting,
    dirty,
  } = formik;

  const handleClick = async () => {
    captureScreenshot();
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setFeedbackComplete(false);
    setFeedbackError(false);
    setImagePreview(null);
    setOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Tooltip title={i18n._('Feedback')} arrow>
          <IconButton
            onClick={handleClick}
            size="small"
            style={{ marginLeft: '8px' }}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <FeedbackIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Dialog
        fullWidth
        maxWidth="md"
        open={open}
        onClose={handleClose}
        aria-labelledby={i18n._('User Feedback Dialog')}
      >
        <DialogTitle>
          <Trans id="Submit Feedback">Submit Feedback</Trans>
        </DialogTitle>

        {!feedbackComplete && (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <DialogContentText>
                <Trans id="Have you encountered a bug? Do you have a suggestion for how we can improve Backfill Planner? Complete the form below to provide feedback">
                  Have you encountered a bug? Do you have a suggestion for how we can improve Backfill Planner?
                  Complete the form below to provide feedback
                </Trans>
              </DialogContentText>

              <Box
                component={Paper}
                sx={{
                  p: 2,
                }}
              >
                <TextField
                  variant="standard"
                  required
                  autoFocus
                  multiline
                  rows={7}
                  fullWidth
                  id="feedback"
                  name="feedback"
                  label={i18n._('Feedback')}
                  value={values.feedback}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.feedback}
                  helperText={errors.feedback}
                />
              </Box>

              {imagePreview && (
                <Box
                  style={{
                    maxWidth: '80vw',
                    maxHeight: '80vh',
                    overflow: 'hidden',
                  }}
                  sx={{
                    mt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={imagePreview}
                    alt="Screenshot preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      display: 'block',
                    }}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Box sx={{
                mb: 1,
              }}
              >
                <ProgressButton variant="contained" color="secondary" type="submit" disabled={!dirty} loading={isSubmitting}>
                  <Trans id="Submit Feedback">Submit Feedback</Trans>
                </ProgressButton>
              </Box>
            </DialogActions>
          </form>
        )}

        {feedbackComplete && (
          <DialogContent>
            <DialogContentText>
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8, // Fixed the string value '8' (should be a number)
                }}
              >
                {!feedbackError && (
                  <Alert severity="success">
                    <Trans id="Your feedback has been sent successfully. Thank you">
                      Your feedback has been sent successfully. Thank you
                    </Trans>
                  </Alert>
                )}

                {feedbackError && (
                  <Alert severity="error">
                    <Trans id="There was an error sending your feedback. Please contact the Mine Smart admin or try again later">
                      There was an error sending your feedback. Please contact the Mine Smart admin or try again later
                    </Trans>
                  </Alert>
                )}
              </Box>
            </DialogContentText>
          </DialogContent>
        )}

      </Dialog>
    </>
  );
};

export default UserFeedbackIcon;
